"use client";

import React from "react";
import { createPortal } from "react-dom";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { DropdownMenuHeader } from "@/components/tailwind/ui/dropdown-menu";
import {
  AnchoredPopover,
  SCROLL_LOCK_ALLOW_ATTR,
  stripPositioningClasses,
  useAnchoredScrollLock,
  type AnchoredPopoverAnchor,
} from "@/components/tailwind/ui/anchored-popover";

interface PropertyInputSheetProps {
  /** Whether the picker is open. Render this component conditionally on the caller side. */
  open: boolean;
  /** Called when the backdrop / X / back button is tapped on mobile, or when Escape is pressed. */
  onClose: () => void;
  /** Mobile sheet header title (e.g. "Status", "Priority", "Date"). */
  title: string;
  /** Tailwind classes that absolutely-position the dropdown on desktop, e.g.
   *  `"absolute top-0 left-0 bg-white dark:bg-background rounded-lg shadow-xl border w-[250px] max-h-[300px] overflow-hidden z-50"`.
   *  Only used when the viewport is `>= md`. */
  desktopAnchorClassName: string;
  /** The actual picker content (search box, option list, etc.). Same JSX is rendered in both layouts. */
  children: React.ReactNode;
  /** When true, the mobile sheet skips the drag-handle + DropdownMenuHeader chrome.
   *  Use this when the child already renders its own header (e.g. EditSinglePropertyModal). */
  hideHeader?: boolean;
  /** Desktop: the trigger element (or a ref to it) to float against. When
   *  passed, the picker is portaled and positioned by AnchoredPopover (never
   *  clipped by overflow-hidden ancestors like the center-peek card, never
   *  off-screen). When omitted, the legacy inline `desktopAnchorClassName`
   *  branch renders — kept for callers that mount inside an existing
   *  popover's flow. */
  anchorEl?: AnchoredPopoverAnchor;
  /** Desktop + anchorEl only: pass false when the caller keeps its own
   *  outside-click/Escape logic (e.g. it coordinates nested legacy menus). */
  manageDismiss?: boolean;
  /** Desktop + anchorEl only: size the panel to the anchor's width. */
  matchAnchorWidth?: boolean;
  /** Desktop + anchorEl only: gap between anchor and panel, px (default 4). */
  offsetPx?: number;
  /** Desktop + anchorEl only: pass false when the panel contains legacy
   *  absolutely-positioned submenus that an `overflow: auto` cap would clip. */
  constrainHeight?: boolean;
}

/**
 * Wraps a property-input picker (the dropdown that opens when the user taps a
 * Status / Select / MultiSelect / Priority / etc. value inside the right
 * sidebar) so it portals to `<body>` as a bottom sheet on mobile, and renders
 * as the original absolutely-positioned floating card on desktop.
 *
 * The component is intentionally dumb — no state, no outside-click handling.
 * The caller owns the `open` flag and passes its own `onClose`. This keeps
 * existing focus / keyboard / outside-click behavior in the individual
 * property inputs untouched on desktop, and adds backdrop tap + back/X buttons
 * on mobile via the portal'd sheet chrome.
 */
export const PropertyInputSheet: React.FC<PropertyInputSheetProps> = ({
  open,
  onClose,
  title,
  desktopAnchorClassName,
  children,
  hideHeader = false,
  anchorEl,
  manageDismiss = true,
  matchAnchorWidth = false,
  offsetPx,
  constrainHeight = true,
}) => {
  const isMobile = useIsMobile();
  useAnchoredScrollLock(open && isMobile);

  if (!open) return null;

  if (isMobile && typeof document !== "undefined") {
    return createPortal(
      <>
        <div
          className="fixed inset-0 z-[10000] bg-black/60"
          onMouseDown={(e) => {
            e.stopPropagation();
            onClose();
          }}
        />
        <div
          role="dialog"
          aria-modal="true"
          {...{ [SCROLL_LOCK_ALLOW_ATTR]: "" }}
          // font-sans + role="dialog": resets the inherited font so UI chrome
          // stays in system sans even when a page sets a serif/mono editor
          // font. The role also matches the chrome-font global reset in
          // globals.css for defense in depth.
          className="sheet-slide-up font-sans fixed inset-x-0 bottom-0 z-[10001] flex flex-col bg-background dark:bg-background rounded-t-lg shadow-lg pb-[max(1rem,env(safe-area-inset-bottom))]"
          style={{ height: "min(80dvh, 640px)" }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {!hideHeader && (
            <>
              <div
                aria-hidden="true"
                className="mx-auto mt-2 mb-1 h-1 w-10 shrink-0 rounded-full bg-muted-foreground/30"
              />
              <DropdownMenuHeader
                title={title}
                onBack={onClose}
                onClose={onClose}
                showBack
                showClose
              />
            </>
          )}
          <div className="flex-1 overflow-y-auto min-h-0 [&>div]:!min-w-0 [&>div]:!max-w-none [&>div]:!w-full [&>div]:!border-0 [&>div]:!rounded-none [&>div]:!shadow-none">
            {children}
          </div>
        </div>
      </>,
      document.body,
    );
  }

  if (anchorEl !== undefined) {
    return (
      <AnchoredPopover
        anchor={anchorEl}
        onClose={manageDismiss ? onClose : undefined}
        matchAnchorWidth={matchAnchorWidth}
        offsetPx={offsetPx}
        constrainHeight={constrainHeight}
        className={stripPositioningClasses(desktopAnchorClassName)}
      >
        {children}
      </AnchoredPopover>
    );
  }

  // font-sans + role="dialog" reset any inherited editor font on the desktop
  // branch — this wrapper renders inline (not portal'd) so without the reset
  // it would pick up the page's serif/mono font.
  return (
    <div role="dialog" className={`modal-pop-in font-sans ${desktopAnchorClassName}`}>
      {children}
    </div>
  );
};
